import * as React from 'react'
import { cn } from '@/lib/utils'

interface ScanInputProps {
  onScan: (barcode: string) => void
  isPending: boolean
  lastError: string | null
  // What kind of error `lastError` is. An unknown barcode is attention (amber):
  // the operator looks at the item. A failed request is a fault (red): the scan
  // did not go through. Defaults to 'not_found' so existing callers are unchanged.
  lastErrorKind?: 'not_found' | 'failed'
  onErrorDismiss: () => void
}

// "Scanbar" look from the V1 wireframe: dashed-border box, large centered
// input. Acts as a giant capture target for keyboard barcode scanners.
//
// IMPORTANT: the input is NOT disabled while a scan is in flight. Disabling
// loses keystrokes from rapid back-to-back scans because USB scanners just
// type-and-Enter without waiting for the previous mutation to settle.
// The mutation queue handles concurrency; the input just keeps accepting input.
//
// A complementary `useBarcodeScanner` hook on the parent catches scans when
// focus drifts to a button — both paths converge on the same `onScan`.
export function ScanInput({
  onScan,
  isPending,
  lastError,
  lastErrorKind = 'not_found',
  onErrorDismiss,
}: ScanInputProps) {
  const failed = lastErrorKind === 'failed'
  const inputRef = React.useRef<HTMLInputElement>(null)
  const [value, setValue] = React.useState('')

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Escape') {
      e.preventDefault()
      setValue('')
      return
    }
    if (e.key !== 'Enter') return
    e.preventDefault()
    const barcode = value.trim()
    if (!barcode) return
    setValue('')
    onErrorDismiss()
    onScan(barcode)
  }

  return (
    <div
      className={cn(
        'flex flex-col items-center gap-1 border-2 border-dashed px-[18px] py-[14px]',
        !lastError
          ? 'border-[var(--text-primary)]'
          : failed
            ? 'border-[var(--status-rejected-fg)]'
            : 'border-[var(--status-awaiting-fg)]'
      )}
      // Click the dashed box anywhere to refocus the input. Helpful when the
      // user clicked a button and the cursor wandered.
      onClick={() => inputRef.current?.focus()}
    >
      <div className="text-[10px] uppercase tracking-[0.06em] text-[var(--mute)]">
        Scan barcode
      </div>
      <div className="flex w-full items-center justify-center gap-2">
        <span
          className={cn(
            'text-[20px] leading-none',
            isPending ? 'text-[var(--status-awaiting-fg)]' : 'text-[var(--text-primary)]'
          )}
          aria-hidden
        >
          ⌨
        </span>
        <input
          ref={inputRef}
          autoFocus
          aria-label="Scan a barcode"
          placeholder={isPending ? 'Working…' : 'Awaiting scan…'}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={handleKeyDown}
          data-testid="scan-input"
          className="w-full max-w-[460px] border-0 bg-transparent text-center text-[22px] font-semibold tracking-[-0.01em] text-[var(--text-primary)] outline-none placeholder:font-normal placeholder:text-[var(--ink-2)]"
        />
      </div>
      <div className="text-[10px] text-[var(--mute)]">
        Scan anywhere on the page · Press Esc to clear · Click rows for manual qty
      </div>
      {lastError && (
        <div
          role="alert"
          data-testid="scan-error"
          className={cn(
            'mt-1 text-[11px] font-medium',
            failed ? 'text-[var(--status-rejected-fg)]' : 'text-[var(--status-awaiting-fg)]'
          )}
        >
          {lastError}
        </div>
      )}
    </div>
  )
}
